import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useGroupDetail } from '@/hooks/useGroups';
import { useGroupBalances } from '@/hooks/useBalances';
import { useGroupMemberLookup } from '@/hooks/useGroupMemberLookup';
import { useCreateSettlement } from '@/hooks/useSettlements';
import { useAuth } from '@/contexts/AuthContext';
import { AmountDisplay } from '@/components/common/AmountDisplay';
import { fromMinorUnits, getMinorUnitsSync } from '@sharecost/shared';
import { Loader2, ArrowLeft, ArrowRight, Handshake } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function SettleUpPage() {
  const { t } = useTranslation();
  const { id: groupId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: group } = useGroupDetail(groupId);
  const { data: balanceData } = useGroupBalances(groupId, group?.currency_code);
  const { data: memberLookup } = useGroupMemberLookup(groupId);
  const createSettlement = useCreateSettlement();

  const [fromUserId, setFromUserId] = useState('');
  const [toUserId, setToUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  if (!group) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  const handleSuggestionClick = (fromId: string, toId: string, amountMinor: number) => {
    setFromUserId(fromId);
    setToUserId(toId);
    const minorUnits = getMinorUnitsSync(group.currency_code);
    setAmount(fromMinorUnits(amountMinor, minorUnits).toString());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = parseFloat(amount);
    if (!fromUserId || !toUserId || isNaN(parsedAmount) || parsedAmount <= 0 || !groupId) return;

    try {
      await createSettlement.mutateAsync({
        groupId,
        fromUserId,
        toUserId,
        amount: parsedAmount,
        currency: group.currency_code,
        note: note || undefined,
      });
      navigate(`/groups/${groupId}`);
    } catch {
      // Error shown via mutation
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 flex items-center gap-1 text-sm text-text-tertiary hover:text-text-secondary"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('web:common.backTo', { name: group.name })}
      </button>

      <h1 className="text-2xl font-bold text-text-primary mb-6">{t('groups:detail.action.settleUp')}</h1>

      {/* Suggestions */}
      {balanceData?.suggestions && balanceData.suggestions.length > 0 && (
        <div className="mb-6 rounded-xl bg-surface-primary p-4 shadow-sm ring-1 ring-border-primary">
          <h3 className="text-sm font-medium text-text-primary mb-3">{t('groups:balances.suggested')}</h3>
          <div className="space-y-2">
            {balanceData.suggestions.map((s, i) => {
              const from = group.members.find(m => m.user_id === s.fromUserId);
              const to = group.members.find(m => m.user_id === s.toUserId);
              const fromName = from?.user?.display_name ?? memberLookup?.get(s.fromUserId)?.displayName ?? t('web:common.unknownUser');
              const toName = to?.user?.display_name ?? memberLookup?.get(s.toUserId)?.displayName ?? t('web:common.unknownUser');
              return (
                <button
                  key={i}
                  onClick={() => handleSuggestionClick(s.fromUserId, s.toUserId, s.amountMinor)}
                  className="flex w-full items-center gap-3 rounded-lg bg-surface-secondary p-3 text-left hover:bg-brand-50 dark:hover:bg-brand-900/30 hover:ring-1 hover:ring-brand-200 transition-all"
                >
                  <span className="text-sm font-medium text-text-secondary">
                    {fromName}
                  </span>
                  <ArrowRight className="h-4 w-4 text-text-quaternary shrink-0" />
                  <span className="text-sm font-medium text-text-secondary">
                    {toName}
                  </span>
                  <AmountDisplay
                    amountMinor={s.amountMinor}
                    currency={group.currency_code}
                    className="ml-auto text-sm font-semibold text-brand-600"
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="rounded-xl bg-surface-primary p-6 shadow-sm ring-1 ring-border-primary space-y-4">
        {createSettlement.error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
            {createSettlement.error instanceof Error ? createSettlement.error.message : t('web:settleUpPage.failedToRecordSettlement')}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-text-secondary">{t('web:settleUpPage.fromPayer')}</label>
            <select
              value={fromUserId}
              onChange={e => setFromUserId(e.target.value)}
              required
              className="mt-1 w-full rounded-lg border border-border-secondary pl-3 pr-10 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            >
              <option value="">{t('web:settleUpPage.selectMember')}</option>
              {group.members.map(m => (
                <option key={m.user_id} value={m.user_id}>
                  {m.user?.display_name}{m.user_id === user?.id ? ` ${t('web:common.youSuffix')}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary">{t('web:settleUpPage.toReceiver')}</label>
            <select
              value={toUserId}
              onChange={e => setToUserId(e.target.value)}
              required
              className="mt-1 w-full rounded-lg border border-border-secondary pl-3 pr-10 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            >
              <option value="">{t('web:settleUpPage.selectMember')}</option>
              {group.members.map(m => (
                <option key={m.user_id} value={m.user_id} disabled={m.user_id === fromUserId}>
                  {m.user?.display_name}{m.user_id === user?.id ? ` ${t('web:common.youSuffix')}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary">
            {t('web:common.amount', { currency: group.currency_code })}
          </label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            required
            placeholder="0.00"
            className="mt-1 w-full rounded-lg border border-border-secondary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary">{t('settlements:section.noteOptional')}</label>
          <input
            type="text"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={t('web:settleUpPage.eGBankTransfer')}
            className="mt-1 w-full rounded-lg border border-border-secondary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 rounded-lg border border-border-secondary px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-surface-hover"
          >
            {t('common:button.cancel')}
          </button>
          <button
            type="submit"
            disabled={createSettlement.isPending || !fromUserId || !toUserId || !amount}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {createSettlement.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Handshake className="h-4 w-4" />
                {t('web:settleUpPage.recordSettlement')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
